// Tópicos MQTT — fonte única compartilhada entre os CLPs/Devices e a bridge
// MQTT→app (apps/worker/src/bridge/). Análogo a QUEUE_NAMES (queue/index.ts):
// nunca montar tópico à mão; sempre passar por estes helpers para garantir o
// mesmo formato nas duas pontas. A ingestão é EXCLUSIVAMENTE por device
// (aposentou o caminho single-sensor `opus <org> sensors...`).

import { z } from "zod";

export const MQTT_TOPIC_PREFIX = "opus";

// --- Ingestão por CLP/Device em lote --------------------------
// Um CLP autentica uma vez e publica UMA mensagem por ciclo de poll com a
// leitura crua por cartão. Formato: opus/<orgId>/devices/<deviceId>/readings
// (plural, espelha o coletivo `sensors`). `use_username_as_clientid` no broker
// força clientid = username = deviceId.

export function deviceReadingsTopic(orgId: string, deviceId: string): string {
  return `${MQTT_TOPIC_PREFIX}/${orgId}/devices/${deviceId}/readings`;
}

export const DEVICE_READINGS_TOPIC_FILTER = `${MQTT_TOPIC_PREFIX}/+/devices/+/readings`;

export function parseDeviceReadingsTopic(
  topic: string
): { orgId: string; deviceId: string } | null {
  const parts = topic.split("/");
  if (parts.length !== 5) return null;
  const [prefix, orgId, devicesSeg, deviceId, readingsSeg] = parts;
  if (prefix !== MQTT_TOPIC_PREFIX) return null;
  if (devicesSeg !== "devices" || readingsSeg !== "readings") return null;
  if (!orgId || !deviceId) return null;
  return { orgId, deviceId };
}

// --- Presença ---------------------------------------------
// O EMQX não permite assinar $events/... diretamente; duas regras do Rule
// Engine no broker (externo — contrato/SQL no runbook interno broker-emqx)
// republicam client_connected/disconnected em QoS 1 para
// opus/events/clients/<clientid>. O payload abaixo é o contrato Opus do
// template das regras — NÃO o formato nativo do EMQX.

export function presenceEventTopic(clientId: string): string {
  return `${MQTT_TOPIC_PREFIX}/events/clients/${clientId}`;
}

// Filtro de assinatura usado pela bridge para receber presença de qualquer device.
export const PRESENCE_EVENT_TOPIC_FILTER = `${MQTT_TOPIC_PREFIX}/events/clients/+`;

// Inverso de presenceEventTopic: extrai { clientId } ou null.
export function parsePresenceEventTopic(topic: string): { clientId: string } | null {
  const parts = topic.split("/");
  if (parts.length !== 4) return null;
  const [prefix, eventsSeg, clientsSeg, clientId] = parts;
  if (prefix !== MQTT_TOPIC_PREFIX) return null;
  if (eventsSeg !== "events" || clientsSeg !== "clients") return null;
  if (!clientId) return null;
  return { clientId };
}

// reason vem de $events/client_disconnected (EMQX 5.x); desconexão limpa
// ("normal") e abrupta valem igual para presença (clarify Q1 da).
export const PresenceEventSchema = z.object({
  event: z.enum(["connected", "disconnected"]),
  clientid: z.string().min(1),
  username: z.string().min(1),
  reason: z.string().optional(),
  ts: z.number().int(),
});
export type PresenceEvent = z.infer<typeof PresenceEventSchema>;
